import { Check, Info, Sparkles } from 'lucide-react';
import { Modal } from '@/components/ui';
import { CHANGELOG, CURRENT_VERSION } from '@/lib/version';
import { EL_KRETSEN_LOGO_URL, SAMIFY_LOGO_BLACK_URL, SAMIFY_HOMEPAGE } from '@/lib/branding';

interface ChangelogModalProps {
  open: boolean;
  onClose: () => void;
}

function formatDate(iso: string) {
  const date = new Date(`${iso}T00:00:00`);
  return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function ChangelogModal({ open, onClose }: ChangelogModalProps) {
  return (
    <Modal open={open} onClose={onClose} title="Vad är nytt i ELvis Hub" size="2xl">
      <div className="flex flex-col max-h-[85vh]">
        {/* Hero header — El-kretsen branded */}
        <div className="px-6 pt-5 pb-5 bg-ink-900 text-surface">
          <div className="flex items-center justify-between gap-6 mb-4">
            <div className="bg-surface rounded-md px-3 py-2">
              <img src={EL_KRETSEN_LOGO_URL} alt="El-kretsen" className="h-6 w-auto" />
            </div>
            <div className="flex items-center gap-2 text-surface/60">
              <Sparkles size={13} strokeWidth={2} />
              <span className="text-[9.5px] font-semibold uppercase tracking-[0.16em]">ELvis Hub</span>
            </div>
          </div>

          <div className="flex items-baseline gap-3">
            <p className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-surface/55 self-center">
              Nuvarande version
            </p>
            <p className="text-[26px] font-semibold tracking-[-0.02em] leading-none tabular-nums">
              v{CURRENT_VERSION}
            </p>
            <p className="text-[12.5px] text-surface/65">{formatDate(CHANGELOG[0].date)}</p>
          </div>

          <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-surface/[0.06] border border-surface/10">
            <Info size={14} className="text-surface/55 shrink-0 mt-0.5" strokeWidth={2} />
            <p className="text-[12.5px] text-surface/80 leading-relaxed">
              Här samlar vi alla förbättringar och nya funktioner vi lägger till i ELvis Hub.
              Senaste uppdateringen står överst. Klicka på versionsnumret längst ner i sidomenyn när du vill komma hit.
            </p>
          </div>
        </div>

        {/* Changelog list */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-7">
          {CHANGELOG.map((entry, idx) => (
            <article key={entry.version} className="relative">
              <header className="mb-3">
                <div className="flex items-center gap-3 flex-wrap mb-2">
                  <span className="text-display text-[18px] text-ink-900 tabular-nums leading-none">
                    v{entry.version}
                  </span>
                  {idx === 0 && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold uppercase tracking-wider">
                      <Check size={10} strokeWidth={3} />
                      Senaste
                    </span>
                  )}
                  <span className="text-[11px] font-bold uppercase tracking-wider text-ink-500">
                    {formatDate(entry.date)}
                  </span>
                </div>
                <h3 className="text-display text-[22px] text-ink-900 leading-tight">
                  {entry.title}
                </h3>
                {entry.summary && (
                  <p className="text-[14px] text-ink-600 leading-relaxed mt-2">{entry.summary}</p>
                )}
              </header>

              <ul className="space-y-2.5 mt-4">
                {entry.highlights.map((h, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-3 rounded-xl bg-ink-50/60 border border-ink-100"
                  >
                    <span className="w-5 h-5 rounded-full bg-surface border border-ink-200 flex items-center justify-center shrink-0 mt-0.5">
                      <Check size={11} strokeWidth={2.5} className="text-brand-500" />
                    </span>
                    <span className="text-[13.5px] text-ink-700 leading-relaxed flex-1">{h}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        {/* Footer — Samify-branded */}
        <div className="px-6 py-5 bg-ink-50 border-t border-ink-200">
          <div className="flex flex-col items-center text-center gap-2">
            <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-ink-500">
              Byggt av
            </span>
            <a
              href={SAMIFY_HOMEPAGE}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center transition-opacity hover:opacity-75"
              aria-label="Samify"
            >
              <img
                src={SAMIFY_LOGO_BLACK_URL}
                alt="Samify"
                className="h-7 w-auto"
              />
            </a>
            <a
              href="mailto:info@samify.se"
              className="text-[11px] font-semibold text-ink-500 hover:text-ink-700 transition-colors"
            >
              info@samify.se
            </a>
          </div>
        </div>
      </div>
    </Modal>
  );
}
